import React, { useContext, useEffect } from 'react';

/// React router dom
import {
  Routes,
  Route,
  Outlet,
  useNavigate,
  useLocation,
} from 'react-router-dom';

/// Css
import './index.css';
import './step.css';

/// Layout
import Nav from './layouts/nav';
import Footer from './layouts/Footer';

import ScrollToTop from './layouts/ScrollToTop';

/// Form
import WizardNomination from './components/Forms/Wizard/WizardNomination';
import WizardRefereeReport from './components/Forms/Wizard/WizardRefereeReport';

/// Nomination Administration
import AdminNomination from './components/AdminNomination';
import SecretaryNomination from './components/SecretaryNomination';
import SecretaryRefereeReport from './components/SecretaryRefereeReport';
import RefereeReport from './components/RefereeReport';
import AdminRefereeReport from './components/AdminRefereeReport';
import Nomination from './components/Nomination';
import NominationViewProposer from './components/NominationViewProposer';
import NominationCandidate from './components/NominationCandidate';
import Committee from './components/Committee';
import SecretaryAndCouncilMember from './components/SecretaryAndCouncilMember.jsx';
import AdminIndependentAssessor from './components/AdminIndependentAssessor';
import AdminActions from './components/AdminActions';
import SecretaryActions from './components/SecretaryActions';
import RefereeReportExtensionRequest from './components/RefereeReportExtensionRequest';
import WizardIndependentAssessor from './components/Forms/Wizard/WizardIndependentAssessor';
import { ThemeContext } from '../context/ThemeContext';
import NominationElect from './components/NominationElect';
import Settings from './components/Settings';
import PermissionDenied from './pages/PermissionDenied';
import { useAdminActionCollections } from '../context/AdminActionCollections';
import { checkPermissionAndNavigate } from '../utils/checkPermissionAndNavigate';
import {
  POST_SUBMIT_REDIRECT_MS,
  MENU_TOGGLE_DELAY_MS,
} from '../utils/constants';

/// Common Collections
import { CommonCollections } from '../App';
import { useLoggedInUserContextProvider } from '../context/LoggedInUserContext';
import { useProposerCollections } from '../context/ProposerCollections';
import { useAdminCollections } from '../context/AdminCollections';
import { useStaticCollections } from '../context/StaticCollections';
import AdminNominationHistory from './components/AdminNominationHistory';
import COIReport from './components/NominationShortlist/COI/COIReport';
import ScoringReport from './components/Reports/ScoringReport';
import CandidateScoresReport from './components/Reports/CandidateScoresReport';
import RankingReport from './components/Reports/RankingReport/RankingReport';
import CouncilMemberAllocation from './components/CouncilMemberAllocation';
import CronJobMonitor from './components/CronJob/CronJobMonitor';

const Markup = () => {
  const { userDetails } = useLoggedInUserContextProvider();
  const { setRefreshProposerCollections } = useProposerCollections();
  const { setRefreshAdminCollections } = useAdminCollections();
  const { enableCommitteeAccess, enableCouncilMembers } =
    useAdminActionCollections();
  const { nominationId, isFormSubmitted, setIsFormSubmitted } =
    useContext(CommonCollections);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      document
        .querySelectorAll('.mm-active, .mm-show, .active-submenu-item')
        .forEach((el) => {
          el.classList.remove(
            'mm-active',
            'mm-show',
            'show',
            'active-submenu-item',
          );
        });
    }, 0);

    return () => clearTimeout(timer);
  }, [userDetails]);

  useEffect(() => {
    if (isFormSubmitted && location.pathname === '/application') {
      const timer = setTimeout(() => {
        setIsFormSubmitted(false);
        setRefreshProposerCollections((prev) => !prev);
        navigate('/nominations');
      }, POST_SUBMIT_REDIRECT_MS);

      return () => clearTimeout(timer);
    }
  }, [isFormSubmitted]);

  useEffect(() => {
    const { pathname } = location;

    if ((pathname === '/login' || pathname === '/') && userDetails) {
      navigate('nominations');
    }

    if (userDetails) {
      checkPermissionAndNavigate(pathname, userDetails, navigate);
    }
  }, [location.pathname, userDetails, navigate]);

  useEffect(() => {
    if (
      isFormSubmitted &&
      location.pathname === '/application-administration'
    ) {
      const timer = setTimeout(() => {
        setIsFormSubmitted(false);
        setRefreshAdminCollections((prev) => !prev);
        navigate('/nomination-administration');
      }, POST_SUBMIT_REDIRECT_MS);

      return () => clearTimeout(timer);
    }
  }, [isFormSubmitted]);

  useEffect(() => {
    if (isFormSubmitted && location.pathname === '/committees') {
      const timer = setTimeout(() => {
        setIsFormSubmitted(false);
        setRefreshAdminCollections((prev) => !prev);
      }, POST_SUBMIT_REDIRECT_MS);

      return () => clearTimeout(timer);
    }
  }, [isFormSubmitted]);

  useEffect(() => {
    if (location.pathname === '/form-view' && !nominationId) {
      navigate('/nominations');
    }

    if (location.pathname === '/form-view-history' && !nominationId) {
      navigate('/nomination-history');
    }
  }, [location.pathname, nominationId, navigate]);

  useEffect(() => {
    if (location.pathname === '/independent-assessor' && !nominationId) {
      setIsFormSubmitted(false);
      navigate('/assessor-administration');
    }
  }, [location.pathname, nominationId, navigate]);

  useEffect(() => {
    if (isFormSubmitted && location.pathname === '/referee-report') {
      setIsFormSubmitted(false);
      navigate('/referee');
    }
  }, [isFormSubmitted, location.pathname, navigate]);

  useEffect(() => {
    if (
      isFormSubmitted &&
      location.pathname === '/referee-report-administration'
    ) {
      setIsFormSubmitted(false);
      navigate('/referee-administration');
    }
  }, [isFormSubmitted, location.pathname, navigate]);

  useEffect(() => {
    if (
      userDetails &&
      !userDetails.roles.includes('admin') &&
      !(userDetails.roles.includes('council-member') && enableCouncilMembers) &&
      !userDetails.roles.includes('secretary') &&
      ((!enableCommitteeAccess &&
        location.pathname === '/assessor-administration') ||
        (!enableCommitteeAccess && location.pathname === '/candidates'))
    ) {
      navigate('/nominations');
    }
  }, [location.pathname, navigate]);

  const allRoutes = [
    // Form
    {
      url: 'application',
      component: <WizardNomination />,
    },
    {
      url: 'application-administration',
      component: <WizardNomination />,
    },
    {
      url: 'nominations',
      component: <Nomination />,
    },
    {
      url: 'candidates',
      component: <NominationCandidate />,
    },

    {
      url: 'scoring-completion',
      component: <ScoringReport />,
    },
    {
      url: 'candidate-scores',
      component: <CandidateScoresReport />,
    },
    {
      url: 'coi-report',
      component: <COIReport />,
    },
    {
      url: 'ranked-candidates',
      component: <RankingReport />,
    },
    {
      url: 'council-member-allocations',
      component: <CouncilMemberAllocation />,
    },
    {
      url: 'form-view',
      component: <NominationViewProposer />,
    },
    {
      url: 'form-view-history',
      component: <NominationViewProposer />,
    },

    {
      url: 'nomination-administration',
      component: <AdminNomination />,
    },
    {
      url: 'nomination-history',
      component: <AdminNominationHistory />,
    },
    {
      url: 'nomination-secretary',
      component: <SecretaryNomination />,
    },
    {
      url: 'referee-report-secretary',
      component: <SecretaryRefereeReport />,
    },
    {
      url: 'referee',
      component: <RefereeReport />,
    },
    {
      url: 'referee-administration',
      component: <AdminRefereeReport mode='administration' />,
    },
    {
      url: 'referee-archive',
      component: <AdminRefereeReport mode='archive' />,
    },
    {
      url: 'referee-report-extension-requests',
      component: <RefereeReportExtensionRequest />,
    },
    {
      url: 'referee-report',
      component: <WizardRefereeReport mode='default' />,
    },
    {
      url: 'referee-report-administration',
      component: <WizardRefereeReport mode='administration' />,
    },
    {
      url: 'referee-report-archive',
      component: <WizardRefereeReport mode='archive' />,
    },
    {
      url: 'committees',
      component: <Committee />,
    },
    {
      url: 'secretaries-and-council-members',
      component: <SecretaryAndCouncilMember />,
    },
    {
      url: 'elect-candidate',
      component: <NominationElect />,
    },
    {
      url: 'assessor-administration',
      component: <AdminIndependentAssessor />,
    },
    {
      url: 'independent-assessor',
      component: <WizardIndependentAssessor />,
    },
    {
      url: 'admin-actions',
      component: <AdminActions />,
    },
    {
      url: 'secretary-actions',
      component: <SecretaryActions />,
    },
    {
      url: 'cron-job-monitor',
      component: <CronJobMonitor />,
    },
    {
      url: 'settings',
      component: <Settings />,
    },
    {
      url: 'permission-denied',
      component: <PermissionDenied />,
    },
  ];

  return (
    <>
      <Routes>
        <Route element={<MainLayout />}>
          {allRoutes.map((data, i) => (
            <Route
              key={i}
              exact
              path={`${data.url}`}
              element={data.component}
            />
          ))}
        </Route>
      </Routes>
      <ScrollToTop />
    </>
  );
};

function MainLayout() {
  const { menuToggle, setMenuToggle, isTourOpen } = useContext(ThemeContext);
  const { isLoading } = useStaticCollections();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isTourOpen) {
      return;
    }

    const timer = setTimeout(() => {
      setMenuToggle(true);
    }, MENU_TOGGLE_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [isLoading, isTourOpen, setMenuToggle]);

  return (
    <div
      id='main-wrapper'
      className={`show ${menuToggle ? 'menu-toggle' : ''}`}
    >
      <Nav />
      <div
        className='content-body'
        style={{ minHeight: window.screen.height - 45 }}
      >
        <div className='container mb-5'>
          <Outlet />
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Markup;
